import { Link } from "react-router-dom";
import { ArrowUpRight, FileText, Calculator, Wrench, MessageCircle, Building, Check } from "lucide-react";
import { PageHero } from "@/pages/Agence";

const SERVICES = [
  {
    icon: FileText,
    title: "Gestion administrative",
    items: [
      "Organisation et tenue des assemblées générales",
      "Rédaction des procès-verbaux",
      "Mise à jour des registres",
      "Gestion des relations entre membres et avec les tiers",
    ],
  },
  {
    icon: Calculator,
    title: "Gestion financière & comptable",
    items: [
      "Établissement du budget prévisionnel",
      "Appel et recouvrement des cotisations",
      "Tenue des comptes",
      "Présentation des bilans annuels",
    ],
  },
  {
    icon: Wrench,
    title: "Gestion technique",
    items: [
      "Suivi de l'entretien et de la maintenance des équipements communs",
      "Coordination des prestataires et des travaux",
      "Veille au bon état des infrastructures communes",
      "Gestion des équipements communs",
    ],
  },
  {
    icon: MessageCircle,
    title: "Conseil aux propriétaires",
    items: [
      "Accompagnement dans la rédaction et mise à jour des statuts",
      "Respect des obligations légales en vigueur",
      "Gestion des litiges entre membres",
      "Médiation et accompagnement",
    ],
  },
];

export default function ASL() {
  return (
    <div data-testid="page-asl">
      <PageHero
        overline="Nos métiers — Gestionnaire ASL"
        title="Confiez la gestion de votre ASL à des professionnels."
        subtitle="Gestion & accompagnement des associations syndicales libres (ASL)"
      />

      {/* Intro */}
      <section className="py-16 sm:py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            <div className="lg:col-span-5">
              <Building size={36} strokeWidth={1.3} className="text-[#B8935A]" />
              <h2 className="font-serif text-3xl lg:text-4xl text-[#0F172A] mt-6 leading-tight">
                Qu'est-ce qu'une <span className="italic">ASL</span> ?
              </h2>
              <span className="gpi-divider mt-6" />
            </div>
            <div className="lg:col-span-7 text-[#475569] leading-relaxed space-y-4 text-[1.02rem]">
              <p>
                L'<strong className="text-[#0F172A]">Association Syndicale Libre</strong> est une structure
                juridique permettant à un groupe de propriétaires de gérer en commun des équipements ou
                espaces partagés&nbsp;: voies privées, réseaux d'assainissement, espaces verts, éclairage
                collectif, ouvrages hydrauliques…
              </p>
              <p>
                Régie par l'<strong className="text-[#0F172A]">ordonnance du 1er juillet 2004</strong> et son
                décret d'application, l'ASL offre une grande souplesse de fonctionnement. Elle implique
                cependant des obligations administratives, comptables et juridiques qui requièrent rigueur
                et expertise.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services grid */}
      <section className="py-16 sm:py-20 bg-[#EFE8DC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-14">
            <span className="font-overline text-[#0F172A]">Notre prestation</span>
            <h2 className="font-serif text-4xl text-[#0F172A] mt-4 leading-tight">
              Une prise en charge complète.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {SERVICES.map((s, i) => (
              <div key={s.title} className="bg-white p-8 lg:p-10 border border-white">
                <div className="flex items-start justify-between">
                  <s.icon size={28} strokeWidth={1.4} className="text-[#B8935A]" />
                  <span className="font-overline text-[#94A3B8]">0{i + 1}</span>
                </div>
                <h3 className="font-serif text-2xl text-[#0F172A] mt-6">{s.title}</h3>
                <ul className="mt-5 space-y-2.5">
                  {s.items.map((it) => (
                    <li key={it} className="flex gap-2.5 text-sm text-[#475569]">
                      <Check size={14} className="text-[#B8935A] mt-0.5 shrink-0" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why us */}
      <section className="py-16 sm:py-20 lg:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <span className="font-overline-lg text-[#B8935A]">Pourquoi nous confier votre ASL ?</span>
            <h2 className="font-serif text-4xl text-[#0F172A] mt-4">
              Parce que gérer une ASL <span className="italic">ne s'improvise pas.</span>
            </h2>
          </div>
          <p className="text-[#475569] mt-10 max-w-3xl mx-auto text-center leading-relaxed text-[1.05rem]">
            Notre cabinet met à votre service une expertise reconnue, une réactivité sans faille et un
            suivi personnalisé. Nous veillons à la <strong className="text-[#0F172A]">pérennité de vos
            équipements collectifs</strong>, à la <strong className="text-[#0F172A]">transparence de la
            gestion financière</strong> et à la <strong className="text-[#0F172A]">sérénité de l'ensemble
            des membres</strong>.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 border-t border-[#E2E8F0] pt-16 text-center">
          <h3 className="font-serif text-2xl lg:text-3xl text-[#0F172A]">
            Président d'ASL, membre du bureau ou propriétaire ?
          </h3>
          <p className="text-[#64748B] mt-4">
            Contactez-nous pour une proposition de gestion adaptée à vos besoins.
          </p>
          <Link
            to="/contact"
            data-testid="page-asl-cta"
            className="gpi-btn-primary mt-8"
          >
            Contactez-nous
            <ArrowUpRight size={14} />
          </Link>
        </div>
      </section>
    </div>
  );
}
